import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import type { QuoteResult } from "@lawnops/shared";
import { getQuoteEngine } from "../lib/quoteEngineFactory";
import { Button } from "../ui/Button";
import { Input } from "../ui/Input";
import { Card, CardHeader } from "../ui/Card";
import { LanguageSwitcher } from "../ui/LanguageSwitcher";

const addressSchema = z.object({
  address: z.string().min(6),
});
type AddressForm = z.infer<typeof addressSchema>;

const leadSchema = z.object({
  name: z.string().min(2),
  email: z.string().email(),
  phone: z.string().optional(),
});
type LeadForm = z.infer<typeof leadSchema>;

type Phase = "address" | "quoting" | "result" | "accepting" | "accepted";

export function QuotePage() {
  const navigate = useNavigate();
  const { t } = useTranslation(["quote", "common"]);
  const [phase, setPhase] = useState<Phase>("address");
  const [quote, setQuote] = useState<QuoteResult | null>(null);
  const [acceptError, setAcceptError] = useState<string | null>(null);

  const addressForm = useForm<AddressForm>({ resolver: zodResolver(addressSchema) });
  const leadForm = useForm<LeadForm>({ resolver: zodResolver(leadSchema) });

  async function onSubmitAddress(values: AddressForm) {
    setPhase("quoting");
    const engine = getQuoteEngine();
    try {
      const result = await engine.quoteFromAddress({ raw: values.address });
      setQuote(result);
      setPhase("result");
    } catch (err) {
      addressForm.setError("address", {
        message: (err as Error).message ?? t("errors.couldNotGenerate"),
      });
      setPhase("address");
    }
  }

  async function onSubmitLead(values: LeadForm) {
    if (!quote) return;
    setPhase("accepting");
    setAcceptError(null);
    try {
      await new Promise((r) => setTimeout(r, 600));
      const stash = {
        ...values,
        address: quote.formattedAddress,
        lawnAreaSqft: quote.lawnAreaSqft,
        totalPrice: quote.totalPrice,
        capturedAt: new Date().toISOString(),
      };
      console.info("[lead stub] would submit:", stash);
      setPhase("accepted");
    } catch (err) {
      setAcceptError((err as Error).message);
      setPhase("result");
    }
  }

  if (phase === "accepted") {
    return (
      <PublicLayout>
        <Card className="w-full max-w-lg text-center">
          <CardHeader title={t("accepted.title")} />
          <p className="text-sm text-slate-600">{t("accepted.body")}</p>
          <div className="mt-6">
            <Button variant="secondary" onClick={() => navigate("/")}>
              {t("accepted.back")}
            </Button>
          </div>
        </Card>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <div className="flex w-full max-w-4xl flex-col gap-6">
        <header className="text-center">
          <h1 className="text-brand-700 text-3xl font-bold">{t("page.title")}</h1>
          <p className="mt-1 text-sm text-slate-500">{t("page.subtitle")}</p>
        </header>

        <Card>
          <form
            onSubmit={addressForm.handleSubmit(onSubmitAddress)}
            className="flex flex-col gap-3 sm:flex-row sm:items-end"
          >
            <div className="flex-1">
              <Input
                label={t("page.addressLabel")}
                placeholder={t("page.addressPlaceholder")}
                autoComplete="street-address"
                disabled={phase === "quoting"}
                error={
                  addressForm.formState.errors.address
                    ? t("common:validation.addressMin")
                    : undefined
                }
                {...addressForm.register("address")}
              />
            </div>
            <Button type="submit" loading={phase === "quoting"} disabled={phase === "quoting"}>
              {phase === "quoting" ? t("page.measuring") : t("page.getQuote")}
            </Button>
          </form>
          <p className="mt-3 text-xs text-slate-400">{t("page.mockBanner")}</p>
        </Card>

        {phase === "result" || phase === "accepting" ? (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader
                title={t("result.satelliteTitle")}
                description={quote!.formattedAddress}
              />
              <img
                src={quote!.satelliteImageUrl}
                alt={t("result.satelliteAlt", { address: quote!.formattedAddress })}
                className="w-full rounded-md"
              />
              <dl className="mt-4 grid grid-cols-2 gap-2 text-sm">
                <dt className="text-slate-500">{t("result.lawnArea")}</dt>
                <dd className="text-right font-medium text-slate-900">
                  {quote!.lawnAreaSqft.toLocaleString()} {t("result.sqftSuffix")}
                </dd>
                {quote!.totalAreaSqft ? (
                  <>
                    <dt className="text-slate-500">{t("result.parcelArea")}</dt>
                    <dd className="text-right text-slate-700">
                      {quote!.totalAreaSqft.toLocaleString()} {t("result.sqftSuffix")}
                    </dd>
                  </>
                ) : null}
                <dt className="text-slate-500">{t("result.generatedIn")}</dt>
                <dd className="text-right text-slate-700">
                  {quote!.durationMs} {t("result.msSuffix")}
                </dd>
              </dl>
            </Card>

            <Card>
              <CardHeader title={t("result.quoteTitle")} />
              <ul className="divide-y divide-slate-100">
                {quote!.lineItems.map((li, i) => (
                  <li key={i} className="flex items-start justify-between py-3">
                    <div>
                      <p className="text-sm font-medium text-slate-900">{li.name}</p>
                      {li.detail ? <p className="text-xs text-slate-500">{li.detail}</p> : null}
                    </div>
                    <span className="ml-4 text-sm font-medium text-slate-900">
                      ${li.price.toFixed(2)}
                    </span>
                  </li>
                ))}
              </ul>
              <div className="mt-4 flex items-center justify-between border-t border-slate-200 pt-3">
                <span className="text-sm text-slate-500">{t("result.total")}</span>
                <span className="text-brand-700 text-lg font-semibold">
                  ${quote!.totalPrice.toFixed(2)}
                </span>
              </div>

              <form
                onSubmit={leadForm.handleSubmit(onSubmitLead)}
                className="mt-6 flex flex-col gap-3"
              >
                <h3 className="text-sm font-semibold text-slate-900">{t("result.claimTitle")}</h3>
                <Input
                  label={t("common:validation.nameMin")}
                  autoComplete="name"
                  disabled={phase === "accepting"}
                  error={
                    leadForm.formState.errors.name ? t("common:validation.nameMin") : undefined
                  }
                  {...leadForm.register("name")}
                />
                <Input
                  label={t("common:validation.email")}
                  type="email"
                  autoComplete="email"
                  disabled={phase === "accepting"}
                  error={leadForm.formState.errors.email ? t("common:validation.email") : undefined}
                  {...leadForm.register("email")}
                />
                <Input
                  label={t("result.phoneOptional")}
                  type="tel"
                  autoComplete="tel"
                  disabled={phase === "accepting"}
                  error={leadForm.formState.errors.phone?.message}
                  {...leadForm.register("phone")}
                />
                {acceptError ? <p className="text-sm text-red-600">{acceptError}</p> : null}
                <Button
                  type="submit"
                  loading={phase === "accepting"}
                  disabled={phase === "accepting"}
                >
                  {t("result.accept")}
                </Button>
              </form>
            </Card>
          </div>
        ) : null}
      </div>
    </PublicLayout>
  );
}

function PublicLayout({ children }: { children: React.ReactNode }) {
  return (
    <main className="flex min-h-screen flex-col items-center justify-start gap-6 bg-slate-50 px-4 py-10">
      {children}
      <LanguageSwitcher />
    </main>
  );
}
